import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

// ============================================================
// Réinitialisation du mot de passe (via le jeton reçu par email).
// ============================================================

type Status = 'checking' | 'invalid' | 'valid' | 'done';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [status, setStatus] = useState<Status>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!token) { setStatus('invalid'); return; }
      try {
        const { data } = await supabase
          .from('password_resets')
          .select('id, user_id, email, expires_at, used')
          .eq('token', token)
          .limit(1);
        const row = data && data.length > 0 ? data[0] : null;
        if (!row || row.used || new Date(row.expires_at).getTime() < Date.now()) {
          setStatus('invalid');
        } else {
          setStatus('valid');
        }
      } catch {
        setStatus('invalid');
      }
    };
    verify();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères.'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }

    setLoading(true);
    try {
      const { data } = await supabase
        .from('password_resets')
        .select('id, user_id, email, expires_at, used')
        .eq('token', token)
        .limit(1);
      const row = data && data.length > 0 ? data[0] : null;
      if (!row || row.used || new Date(row.expires_at).getTime() < Date.now()) {
        setStatus('invalid');
        return;
      }

      const { default: bcrypt } = await import('bcryptjs');
      const salt = bcrypt.genSaltSync(10);
      const hashed = bcrypt.hashSync(password, salt);

      const { error: updErr } = await supabase
        .from('users')
        .update({ password: hashed })
        .eq('id', row.user_id);
      if (updErr) throw updErr;

      await supabase.from('password_resets').update({ used: true }).eq('id', row.id);
      setStatus('done');
      setTimeout(() => navigate('/login'), 2600);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la réinitialisation.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full h-11 px-4 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors';

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar forceScrolled={true} />
      <main className="flex-1 flex items-center justify-center px-4 pt-20 pb-10">
        <div className="w-full max-w-md">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
            {status === 'checking' && (
              <div className="flex flex-col items-center py-8 gap-3">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
                <span className="text-sm text-foreground-500">Vérification du lien...</span>
              </div>
            )}

            {status === 'invalid' && (
              <div className="text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                  <i className="ri-error-warning-line text-2xl text-red-500"></i>
                </div>
                <h2 className="text-lg font-bold font-heading text-foreground-950">Lien invalide ou expiré</h2>
                <p className="text-sm text-foreground-500">Ce lien de réinitialisation n&apos;est plus valide. Demandez-en un nouveau.</p>
                <Link to="/mot-de-passe-oublie" className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium">
                  <i className="ri-refresh-line"></i>Nouveau lien
                </Link>
              </div>
            )}

            {status === 'done' && (
              <div className="text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto">
                  <i className="ri-check-line text-2xl text-accent-600"></i>
                </div>
                <h2 className="text-lg font-bold font-heading text-foreground-950">Mot de passe modifié</h2>
                <p className="text-sm text-foreground-500">Redirection vers la connexion...</p>
              </div>
            )}

            {status === 'valid' && (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-xl font-bold font-heading text-foreground-950 mb-1.5">Nouveau mot de passe</h1>
                  <p className="text-sm text-foreground-500">Choisissez un mot de passe sécurisé.</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 rounded-md bg-red-50/80 border border-red-100 text-red-700 text-sm flex items-start gap-2">
                      <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i><span>{error}</span>
                    </div>
                  )}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">Mot de passe</label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="6 caractères minimum"
                        required
                        className={`${inputClass} pr-10`}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer">
                        <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="confirm" className="block text-sm font-medium text-foreground-700 mb-1.5">Confirmer</label>
                    <input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmez votre mot de passe" required className={inputClass} />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (<><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>) : 'Réinitialiser'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}